import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiAxios } from "../../utils/apiAxios";
import { CompleteOrderRow } from "./../DentistHub/interface/CompleteOrderRow";


function formatDuration(fromStr?: string, toStr?: string): string {
  if (!fromStr || !toStr || fromStr === "-" || toStr === "-") return "-";

  const from = new Date(fromStr);
  const to = new Date(toStr);
  const diffMs = to.getTime() - from.getTime();
  if (isNaN(diffMs) || diffMs <= 0) return "-";

  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function StarPicker({
  value,
  onChange,
}: {
  value: number;
  onChange: (n: number) => void;
}) {
  const [hover, setHover] = useState < number | null > (null);
  const display = hover ?? value;

  return (
    <div className="rc-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <i
          key={n}
          role="button"
          aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(null)}
          onClick={() => onChange(n)}
          className={`bi ${n <= display ? "bi-star-fill" : "bi-star"} rc-star`}
        />
      ))}
    </div>
  );
}


function formatDateTime(dateStr?: string): string {
  if (!dateStr || dateStr === "-") return "-";

  const date = new Date(dateStr);

  if (isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

function RatingRow({
  icon,
  label,
  meta,
  value,
  onChange,
}: {
  icon: string;
  label: string;
  meta?: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="rc-row">
      <div className="rc-row-left">
        <span className="rc-icon">
          <i className={`bi ${icon}`} />
        </span>
        <div>
          <div className="rc-row-label">{label}</div>
          {meta && <div className="rc-row-meta">{meta}</div>}
        </div>
      </div>
      <StarPicker value={value} onChange={onChange} />
    </div>
  );
}

function RatingModal({
  row,
  initialRatings,
  onClose,
  onSave,
}: {
  row: CompleteOrderRow;
  initialRatings: { designer: number; milling: number; delivery: number };
  onClose: () => void;
  onSave: (ratings: { designer: number; milling: number; delivery: number }) => void;
}) {
  const [ratings, setRatings] = useState(initialRatings);
  const setField = (field: keyof typeof ratings) => (n: number) =>
    setRatings((prev) => ({ ...prev, [field]: n }));
  const isDesignService = row.orderTypeName === "Design Service";
  const filled = isDesignService
    ? [ratings.designer].filter(v => v > 0)
    : [ratings.designer, ratings.milling, ratings.delivery].filter(v => v > 0);
  const overall = filled.length ? filled.reduce((a, b) => a + b, 0) / filled.length : 0;
  const allRated = isDesignService
    ? ratings.designer > 0
    : ratings.designer > 0 &&
    ratings.milling > 0 &&
    ratings.delivery > 0;

  return (
    <>
      <div className="rc-backdrop" onClick={onClose} />

      <div className="rc-modal-wrap" role="dialog" aria-modal="true" aria-labelledby="rcTitle">
        <div className="rc-modal">
          <button className="rc-close" aria-label="Close" onClick={onClose}>
            <i className="bi bi-x-lg" />
          </button>

          <div className="rc-header">
            <div className="rc-header-badge">
              <i className="bi bi-award" />
            </div>
            <h5 id="rcTitle" className="rc-title">Rate this order</h5>
            <p className="rc-subtitle">Order #{row.orderId}</p>
          </div>

          <div className="rc-body">
            <RatingRow
              icon="bi-person-badge"
              label="Designer"
              //meta="Completed in 2d 5h • 04:30 PM"
              value={ratings.designer}
              onChange={setField("designer")}
            />
            {!isDesignService && (
              <>
                <RatingRow
                  icon="bi-gear-wide-connected"
                  label="Milling"
                  value={ratings.milling}
                  onChange={setField("milling")}
                />

                <RatingRow
                  icon="bi-truck"
                  label="Delivery"
                  value={ratings.delivery}
                  onChange={setField("delivery")}
                />
              </>
            )}
          </div>

          <div className="rc-overall">
            <span>Overall</span>
            <span className="rc-overall-value">
              <i className="bi bi-star-fill" /> {overall ? overall.toFixed(1) : "—"}
            </span>
          </div>

          <div className="rc-footer">
            <button className="rc-btn rc-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              className="rc-btn rc-btn-primary"
              disabled={!allRated}
              onClick={() => onSave(ratings)}
            >
              <i className="bi bi-check2" /> Save rating
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export function RatingCell({
  row,
  onRated,
}: {
  row: CompleteOrderRow;
  onRated: (rating: number) => void;
}) {

  const [open, setOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const isDesignService = row.orderTypeName === "Design Service";
  // const [alreadyRated, setAlreadyRated] = useState(false);
  const hasRating =
    row.overallRating !== null &&
    row.overallRating !== undefined;

  const [ratings, setRatings] = useState({

    designer: 0,

    milling: 0,

    delivery: 0

  });

  const handleSave = async (next: typeof ratings) => {
    try {

      setLoading(true);
      const overallRating = isDesignService
        ? next.designer
        : Number(
          (
            (next.designer + next.milling + next.delivery) / 3
          ).toFixed(1)
        );
      const payload = {
        caseID: row.caseId,
        clinicId: row.clinicID,
        designRating: next.designer,
        millingRating: isDesignService ? null : next.milling,
        deliveryRating: isDesignService ? null : next.delivery,
        overAllRating: overallRating
      };

      // console.log("payload",payload)
      const response = await apiAxios.post("/Rating/Create",
        payload
      );
      if (response.data.isSuccess) {

        toast.success("Rating submitted successfully.");

        setRatings(next);

        onRated(overallRating);

        setOpen(false);

        return;
      }
      else {
        toast.error(response.data.status);

      }

    }

    catch {
      toast.error("Unable to submit rating.");
    }

    finally {
      setLoading(false);

    }

  };

  // const filled = [

  //     ratings.designer,

  //     ratings.milling,

  //     ratings.delivery

  // ].filter(x => x > 0);
  const filled = isDesignService
    ? [ratings.designer].filter(v => v > 0)
    : [ratings.designer, ratings.milling, ratings.delivery].filter(v => v > 0);
  const avg =

    filled.length > 0

      ? filled.reduce((a, b) => a + b, 0) / filled.length

      : 0;
  return (
    <>
      <button
        type="button"
        className={`rc-pill ${hasRating ? "rc-pill-rated" : "rc-pill-empty"
          }`}
        disabled={hasRating}
        onClick={() => {
          if (!hasRating) {
            setOpen(true);
          }
        }}
      >
        <i className="bi bi-star-fill" />

        {
          hasRating
            ? row.overallRating?.toFixed(1)
            : "Rate"
        }
      </button>

      {

        open && (

          <RatingModal

            row={row}

            initialRatings={ratings}

            onClose={() => setOpen(false)}

            onSave={handleSave}

          />

        )

      }
    </>
  );
}
